import { useCallback, useEffect, useState, useRef } from 'react';
import { useDeviceDetection } from '../../../../hooks/UseDeviceDetection';
import { useViewInfo } from '../../../../hooks/UseViewInfo';
import { Flex, View } from '@adobe/react-spectrum';
import { useDependency } from '../../../../contexts/DependencyProvider';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../state/store';
import { useTranslation } from 'react-i18next';
import FilterViewsComponent from '../../service-activities/components/FilterViewsComponent';
import FilterControlsComponent from '../../service-activities/components/FilterControlsComponent';
import QuickFilterControlsComponent from '../../service-activities/components/QuickFilterControlsComponent';
import { CustomView } from '../../../../services/soap/dataObjects/CustomView';
import { IFilterControl } from '../../../../components/protected/Dialogs/components/FilterSetupPlaceholder/FilterSetupPlaceholder';
import { Order } from '../../../../services/soap/dataObjects/Order';
import type { IFilterComponentProps, QueryParams, FilterColumn } from '../types';
import styles from './filter_component.module.css';

interface FilterParam {
  name: string;
  type: string;
  value: string;
}

interface FilterParamsState {
  FILTER: { COL: FilterParam[] };
  FIELDS?: { field: string[] };
  ORDER?: Order;
}

const FilterComponent: React.FC<IFilterComponentProps> = ({
  onFilterChange,
  filterParams,
  filterStyle,
  customViews = [],
  hasMoreCustomViews = false,
  onLoadMoreCustomViews,
  onRefreshCustomViews,
  formSettings = [],
  fpaTypeCategory = '',
  initialViewId,
  onCreateView,
  onEditView,
  onDuplicateView,
  onDeleteViewClick,
  resetBaselineRef: resetBaselineRefProp,
  showEmptyViews = false,
  deviceInfo: deviceInfoProp,
}) => {
  const { t } = useTranslation();
  const allTypesLabel = t('all_types', { ns: 'layout_components', defaultValue: 'All types' });
  const allStatesLabel = t('all_states', { ns: 'layout_components', defaultValue: 'All states' });
  const allUsersLabel = t('all_users', { ns: 'layout_components', defaultValue: 'All users' });
  const selectedApp = useSelector((state: RootState) => state.userWebLayout.selectedAppId);
  const { store, featureService } = useDependency();
  const { isMobile } = useViewInfo();
  const deviceInfoFromHook = useDeviceDetection(isMobile);
  const deviceInfo = deviceInfoProp ?? deviceInfoFromHook;
  const [show, setShow] = useState(false);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [selectedView, setSelectedView] = useState<string>(() => {
    const col = filterParams?.FILTER?.COL?.find((c: FilterColumn) => c?.name === 'state');
    return col?.value != null ? String(col.value) : '';
  });
  const [viewFilterControls, setViewFilterControls] = useState<IFilterControl[]>([]);
  const getPersistedFiltersRef = useRef<((viewId: string) => unknown[] | null) | null>(null);
  const resetBaselineRefInternal = useRef<(() => void) | null>(null);
  const resetBaselineRef = resetBaselineRefProp ?? resetBaselineRefInternal;
  const didApplyInitialViewRef = useRef<boolean>(false);

  const shouldUseMobileLayout = deviceInfo?.shouldUseMobileLayout?.() ?? deviceInfoFromHook.shouldUseMobileLayout();
  const isMobileOrSmallScreen =
    shouldUseMobileLayout || deviceInfo?.isIPad || deviceInfo?.isTablet || deviceInfoFromHook?.isIPad || deviceInfoFromHook?.isTablet;

  useEffect(() => {
    const col = filterParams?.FILTER?.COL?.find((c: FilterColumn) => c?.name === 'state');
    const newSelectedView = col?.value != null ? String(col.value) : '';
    if (newSelectedView !== selectedView) setSelectedView(newSelectedView);
  }, [filterParams, selectedView]);

  const handleClose = () => setShow(false);
  const handleShow = () => {
    setShow(true);
    const col = filterParams?.FILTER?.COL?.find((c: FilterColumn) => c?.name === 'state');
    setSelectedView(col?.value != null ? String(col.value) : '');
  };

  const handleViewSelect = useCallback(
    (view: CustomView | null) => {
      if (view === null) {
        setSelectedView('');
        setViewFilterControls([]);
        const { FIELDS: _f, ORDER: _o, ...rest } = filterParams;
        const updatedParams: QueryParams = { ...rest, FILTER: { COL: [] } };
        onFilterChange(updatedParams);
        return;
      }
      setSelectedView(view.id || '');
      const updatedParams: FilterParamsState = { FILTER: { COL: [] } };
      if (view.definition) {
        try {
          const definition = JSON.parse(view.definition);
          if (definition.columns?.length) {
            const fields = definition.columns
              .filter((col: { isVisible?: boolean }) => col.isVisible !== false)
              .map((col: { itemname?: string }) => col.itemname)
              .filter((itemname: string) => itemname != null && itemname !== '');
            if (fields.length > 0) updatedParams.FIELDS = { field: fields };
          }
          if (definition.sorting?.length) {
            updatedParams.ORDER = {
              COL: definition.sorting.map((sort: { itemname?: string; level?: string }) => {
                let sortName = sort.itemname || '';
                if (sortName.includes('|')) {
                  const parts = sortName.split('|');
                  sortName = parts[1]?.trim() || sortName;
                }
                return { name: sortName, desc: (sort.level === 'Descending' ? 'true' : 'false') as 'true' | 'false' };
              }),
            };
          }
          if (definition.columns?.length) {
            const filterableColumns = definition.columns.filter((col: { isFilter?: boolean }) => col?.isFilter === true);
            interface FilterDef { itemname?: string; name?: string; filterInfo?: unknown[] }
            const filtersByItemname: Record<string, FilterDef> = {};
            const filtersByName: Record<string, FilterDef> = {};
            (definition.filters || []).forEach((f: FilterDef) => {
              if (f?.itemname) filtersByItemname[f.itemname] = f;
              if (f?.name) {
                const filterName = f.name.includes('|') ? f.name.split('|')[0]?.trim() || f.name : f.name;
                filtersByName[filterName] = f;
              }
            });
            const controls: IFilterControl[] = filterableColumns
              .map((column: { itemname?: string; type?: string; label?: string }) => {
                if (!column) return null;
                const matchingFormSetting = formSettings?.find((item) => item.itemname === column.itemname);
                const columnId = matchingFormSetting ? String(matchingFormSetting.id) : '';
                const matchingFilter = filtersByItemname[column.itemname || ''] || filtersByName[column.itemname || ''];
                let filterInfo: { name: string; type: string; value: string; negation?: boolean }[] = [{ name: 'value', type: 'equals', value: '' }];
                if (matchingFilter?.filterInfo?.length) {
                  filterInfo = (matchingFilter.filterInfo as { type?: string; value?: string; isCurrentUser?: boolean }[]).map((fi) => {
                    let filterValue = fi.value || '';
                    if (fi.isCurrentUser && store) filterValue = String(store.UserId);
                    let filterType = fi.type || 'equals';
                    let negation = false;
                    if (fi.type === 'is_empty') { filterType = 'empty'; negation = false; }
                    else if (fi.type === 'is_not_empty') { filterType = 'empty'; negation = true; }
                    return { name: 'value', type: filterType, value: filterValue, negation };
                  });
                }
                return {
                  name: column.itemname || '',
                  dataType: column.type || 'text',
                  label: column.label || column.itemname || '',
                  data: { id: columnId, itemname: column.itemname || '', label: column.label || column.itemname || '', isVisible: true, isFilter: true, type: column.type || 'text' },
                  filterInfo,
                };
              })
              .filter((c: IFilterControl | null): c is IFilterControl => c !== null);
            setViewFilterControls(controls);
            (definition.filters || []).forEach((filterDef: { name?: string; filterInfo?: { type?: string; value?: string; isCurrentUser?: boolean }[] }) => {
              const filterInfo = filterDef?.filterInfo?.[0];
              if (!filterInfo && filterDef.filterInfo?.[0]?.isCurrentUser !== true) return;
              const isEmptyOp = filterInfo?.type === 'is_empty' || filterInfo?.type === 'is_not_empty';
              if (!isEmptyOp && !filterInfo?.value && filterInfo?.isCurrentUser !== true) return;
              let filterName = filterDef.name || '';
              if (filterName.includes('|')) filterName = filterName.split('|')[0]?.trim() || filterName;
              let filterType = (filterInfo?.type || 'equals') as FilterColumn['type'];
              if (filterInfo?.type === 'is_empty') filterType = 'empty';
              else if (filterInfo?.type === 'is_not_empty') filterType = 'empty';
              let filterValue = filterInfo?.value || '';
              if (filterInfo?.isCurrentUser && store) filterValue = String(store.UserId);
              updatedParams.FILTER.COL.push({ name: filterName, type: filterType, value: filterValue });
            });
          }
        } catch (e) {
          console.error('Error parsing view definition:', e);
        }
      }
      updatedParams.FILTER.COL.push({ name: 'state', type: 'equals' as const, value: view.id || '' });
      onFilterChange(updatedParams as QueryParams);
    },
    [filterParams, formSettings, onFilterChange, store]
  );

  useEffect(() => {
    if (didApplyInitialViewRef.current) return;
    const currentlySelected = filterParams?.FILTER?.COL?.find((col: { name: string }) => col?.name === 'state')?.value;
    if (currentlySelected) { didApplyInitialViewRef.current = true; return; }
    const candidateId = initialViewId ? String(initialViewId).trim() : '';
    if (!candidateId) return;
    const view = customViews.find((v) => String(v.id ?? '') === candidateId);
    if (!view) return;
    didApplyInitialViewRef.current = true;
    handleViewSelect(view);
  }, [customViews, filterParams, handleViewSelect, initialViewId]);

  const createParamsCustomViews = useCallback(
    async (filterTypeName: string, filterValue: unknown) => {
      const filterValueStr: string =
        filterTypeName === 'view' ? String(filterValue ?? '') : Array.from((filterValue as Iterable<unknown>) || []).join(',');
      const updateFilterParams = (params: FilterParam | FilterParam[]) => {
        const paramArray = Array.isArray(params) ? params : [params];
        let updatedArray = [...filterParams.FILTER.COL];
        paramArray.forEach(({ name, type, value }) => {
          const idx = updatedArray.findIndex((obj) => obj.name === name);
          const colType = type as FilterColumn['type'];
          if (idx !== -1) updatedArray[idx] = { ...updatedArray[idx], type: colType, value };
          else updatedArray.push({ name, type: colType, value: String(value ?? '') });
        });
        filterParams.FILTER.COL = updatedArray;
      };
      switch (filterTypeName) {
        case 'type':
          updateFilterParams({ name: 'projectType', type: 'equals', value: filterValueStr === allTypesLabel ? '' : filterValueStr });
          break;
        case 'view':
          filterParams.FILTER.COL = filterParams.FILTER.COL.filter((col) => col.name !== 'state' && col.name !== 'ownerId');
          updateFilterParams({ name: 'state', type: 'equals', value: (filterValue as string) || '' });
          break;
        case 'customState':
          updateFilterParams({ name: 'customState', type: 'in', value: filterValueStr === allStatesLabel ? '' : filterValueStr });
          break;
        case 'user':
          updateFilterParams({ name: 'ownerId', type: 'equals', value: filterValueStr === allUsersLabel ? '' : filterValueStr });
          break;
        default:
          break;
      }
      filterParams.FILTER.COL = filterParams.FILTER.COL.filter((col) => String(col.value ?? '') !== '');
      onFilterChange(filterParams as QueryParams);
    },
    [allStatesLabel, allTypesLabel, allUsersLabel, filterParams, onFilterChange]
  );

  const emptyStateOnly = showEmptyViews || (customViews?.length ?? 0) === 0;

  return isMobileOrSmallScreen ? (
    <View position="relative" width="100%" height="64px" UNSAFE_className={styles.mobile_header_parent}>
      {!emptyStateOnly && (
        <Flex direction="column" position="absolute" top="calc(50% - 16px)" left="58px" alignItems="start" justifyContent="start">
          <View position="relative" UNSAFE_className={styles.menu_heading_text}>
            {customViews.find((v) => v.id === selectedView)?.name || t('project_activities', { ns: 'layout_components' })}
          </View>
        </Flex>
      )}
      <View position="absolute" top="calc(50% - 12px)" left="16px" width="24px" height="24px" overflow="hidden">
        <span onClick={handleShow} className="me-2">
          <i className="bi bi-list"></i>
        </span>
        <FilterViewsComponent
          views={customViews}
          selectedViewId={selectedView}
          onViewSelect={handleViewSelect}
          onCreateView={onCreateView}
          onEditView={onEditView}
          onDuplicateView={onDuplicateView}
          onDeleteViewClick={onDeleteViewClick}
          onLoadMore={onLoadMoreCustomViews}
          onViewsRefresh={onRefreshCustomViews}
          hasMore={hasMoreCustomViews}
          isMobile={true}
          show={show}
          onClose={handleClose}
          onShow={handleShow}
          store={store}
          createParams={createParamsCustomViews}
          formSettings={formSettings}
          deviceInfo={deviceInfoFromHook}
          filterParams={filterParams}
          getPersistedFiltersRef={getPersistedFiltersRef}
          resetBaselineRef={resetBaselineRef}
          emptyStateLabel={t('create_first_project_view', { ns: 'layout_components', defaultValue: 'Create your first Project View' })}
          emptyStateOnly={emptyStateOnly}
        />
      </View>
      {!emptyStateOnly && (
      <FilterControlsComponent
        filterControls={viewFilterControls}
        filterParams={filterParams}
        onFilterChange={onFilterChange as unknown as (fp: Record<string, unknown>) => void}
        createParams={createParamsCustomViews}
        isMobile={true}
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        onOpen={() => setIsOpen(true)}
        showDefaultFilters={true}
        fpaTypeCategory={fpaTypeCategory}
        formSettings={formSettings}
        selectedView={customViews.find((v) => v.id === selectedView) || undefined}
        resetBaselineRef={resetBaselineRef}
      />
      )}
    </View>
  ) : (
    <View width="100%" position="relative" backgroundColor="static-white" UNSAFE_className={styles.filter_menu_parent} UNSAFE_style={filterStyle}>
      <Flex direction="column" alignItems="start" gap="size-200" width="100%">
        <FilterViewsComponent
          views={customViews}
          selectedViewId={selectedView}
          onViewSelect={handleViewSelect}
          onCreateView={onCreateView}
          onEditView={onEditView}
          onDuplicateView={onDuplicateView}
          onDeleteViewClick={onDeleteViewClick}
          onLoadMore={onLoadMoreCustomViews}
          onViewsRefresh={onRefreshCustomViews}
          hasMore={hasMoreCustomViews}
          isMobile={false}
          store={store}
          createParams={createParamsCustomViews}
          formSettings={formSettings}
          deviceInfo={deviceInfoFromHook}
          filterParams={filterParams}
          getPersistedFiltersRef={getPersistedFiltersRef}
          resetBaselineRef={resetBaselineRef}
          emptyStateLabel={t('create_first_project_view', { ns: 'layout_components', defaultValue: 'Create your first Project View' })}
          emptyStateOnly={emptyStateOnly}
        />
        {!emptyStateOnly && (
        <FilterControlsComponent
          filterControls={viewFilterControls}
          filterParams={filterParams}
          onFilterChange={onFilterChange as unknown as (fp: Record<string, unknown>) => void}
          createParams={createParamsCustomViews}
          isMobile={false}
          showDefaultFilters={false}
          fpaTypeCategory={fpaTypeCategory}
          selectedView={customViews.find((v) => v.id === selectedView) || undefined}
          resetBaselineRef={resetBaselineRef}
        />
        )}
        {!emptyStateOnly && (
        <QuickFilterControlsComponent
          formSettings={formSettings}
          filterParams={filterParams}
          onFilterChange={onFilterChange as unknown as (fp: Record<string, unknown>) => void}
          fpaTypeCategory={fpaTypeCategory}
        />
        )}
      </Flex>
    </View>
  );
};

export default FilterComponent;
